import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  Tabs,
  Tab,
  InputLabel,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  AccountBalance as BalanceIcon
} from '@mui/icons-material';
import axios from '../utils/axiosConfig';

interface SenderName {
  name: string;
  isDefault: boolean;
  isActive: boolean;
}

interface Provider {
  _id: string;
  name: string;
  identifier: string;
  providerType: string;
  isActive: boolean;
  isDefault: boolean;
  apiConfig: {
    baseUrl: string;
    bearerToken?: string;
    senderNames: SenderName[];
  };
  sendingSettings: {
    maxRecipientsPerMessage: number;
    enableRetry: boolean;
    maxRetryAttempts: number;
    retryDelayMinutes: number;
  };
  notifications: {
    notifyOnSuccess: boolean;
    notifyOnFailure: boolean;
    notificationEmail: string;
  };
  accountInfo: {
    balance: number;
    currency: string;
    lastBalanceUpdate?: Date;
  };
  statistics: {
    totalMessagesSent: number;
    totalSuccessfulMessages: number;
    totalFailedMessages: number;
    totalCost: number;
  };
  notes?: string;
}

const SmsProvidersPage: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // حالة النموذج
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    identifier: '',
    providerType: 'taqnyat',
    isActive: true,
    isDefault: false,
    apiConfig: {
      baseUrl: 'https://api.taqnyat.sa',
      bearerToken: '',
      senderNames: [] as SenderName[]
    },
    sendingSettings: {
      maxRecipientsPerMessage: 100,
      enableRetry: true,
      maxRetryAttempts: 3,
      retryDelayMinutes: 5
    },
    notifications: {
      notifyOnSuccess: false,
      notifyOnFailure: true,
      notificationEmail: ''
    },
    notes: ''
  });
  
  // حالة إضافة اسم مرسل
  const [openSenderDialog, setOpenSenderDialog] = useState(false);
  const [newSenderName, setNewSenderName] = useState('');
  const [senderIsDefault, setSenderIsDefault] = useState(false);

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/marketing/sms/api/providers');
      if (response.data.success) {
        setProviders(response.data.providers || response.data.data?.providers || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل المزودين');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (provider?: Provider) => {
    if (provider) {
      // تحميل بيانات المزود الكاملة بما في ذلك Bearer Token
      try {
        setLoading(true);
        const response = await axios.get(`/marketing/sms/api/providers/${provider._id}`);
        if (response.data.success) {
          const fullProvider = response.data.data.provider;
          setEditingProvider(fullProvider);
          setFormData({
            name: fullProvider.name,
            identifier: fullProvider.identifier,
            providerType: fullProvider.providerType || 'taqnyat',
            isActive: fullProvider.isActive,
            isDefault: fullProvider.isDefault,
            apiConfig: {
              baseUrl: fullProvider.apiConfig.baseUrl,
              bearerToken: fullProvider.apiConfig.bearerToken || '',
              senderNames: fullProvider.apiConfig.senderNames
            },
            sendingSettings: fullProvider.sendingSettings,
            notifications: fullProvider.notifications || {
              notifyOnSuccess: false,
              notifyOnFailure: true,
              notificationEmail: ''
            },
            notes: fullProvider.notes || ''
          });
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل بيانات المزود');
      } finally {
        setLoading(false);
      }
    } else {
      setEditingProvider(null);
      setFormData({
        name: '',
        identifier: '',
        providerType: 'taqnyat',
        isActive: true,
        isDefault: false,
        apiConfig: {
          baseUrl: 'https://api.taqnyat.sa',
          bearerToken: '',
          senderNames: []
        },
        sendingSettings: {
          maxRecipientsPerMessage: 100,
          enableRetry: true,
          maxRetryAttempts: 3,
          retryDelayMinutes: 5
        },
        notifications: {
          notifyOnSuccess: false,
          notifyOnFailure: true,
          notificationEmail: ''
        },
        notes: ''
      });
    }
    setDialogTab(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProvider(null);
  };

  const handleSaveProvider = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingProvider) {
        // تحديث مزود موجود
        const response = await axios.put(
          `/marketing/sms/api/providers/${editingProvider._id}`,
          formData
        );
        if (response.data.success) {
          setSuccess('تم تحديث المزود بنجاح');
          loadProviders();
          handleCloseDialog();
        }
      } else {
        // إنشاء مزود جديد
        const response = await axios.post('/marketing/sms/api/providers', formData);
        if (response.data.success) {
          setSuccess('تم إضافة المزود بنجاح');
          loadProviders();
          handleCloseDialog();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء حفظ المزود');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProvider = async (providerId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المزود؟')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(`/marketing/sms/api/providers/${providerId}`);
      if (response.data.success) {
        setSuccess('تم حذف المزود بنجاح');
        loadProviders();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء حذف المزود');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (providerId: string) => {
    setLoading(true);
    try {
      const response = await axios.put(`/marketing/sms/api/providers/${providerId}/set-default`);
      if (response.data.success) {
        setSuccess('تم تعيين المزود كافتراضي بنجاح');
        loadProviders();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء تعيين المزود');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSenderName = async () => {
    if (!newSenderName.trim()) {
      setError('يجب إدخال اسم المرسل');
      return;
    }

    const newSender: SenderName = {
      name: newSenderName.trim(),
      isDefault: senderIsDefault,
      isActive: true
    };

    setFormData({
      ...formData,
      apiConfig: {
        ...formData.apiConfig,
        senderNames: [...formData.apiConfig.senderNames, newSender]
      }
    });

    setNewSenderName('');
    setSenderIsDefault(false);
    setOpenSenderDialog(false);
  };

  const handleRemoveSenderName = (index: number) => {
    const updatedSenders = [...formData.apiConfig.senderNames];
    updatedSenders.splice(index, 1);
    setFormData({
      ...formData,
      apiConfig: {
        ...formData.apiConfig,
        senderNames: updatedSenders
      }
    });
  };

  const handleCheckBalance = async (providerId: string) => {
    setLoading(true);
    try {
      const response = await axios.get(`/marketing/sms/api/providers/${providerId}/balance`);
      if (response.data.success) {
        setSuccess(`الرصيد: ${response.data.data.balance} ${response.data.data.currency}`);
        loadProviders();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء التحقق من الرصيد');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            مزودو خدمة SMS
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            إضافة مزود جديد
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading && !openDialog ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {providers.map((provider) => (
              <Grid item xs={12} md={6} key={provider._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="div">
                          {provider.name}
                          {provider.isDefault && (
                            <Chip
                              icon={<StarIcon />}
                              label="افتراضي"
                              color="primary"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {provider.identifier}
                        </Typography>
                      </Box>
                      <Chip
                        icon={provider.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                        label={provider.isActive ? 'نشط' : 'غير نشط'}
                        color={provider.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="body2" gutterBottom>
                      <strong>الرصيد:</strong> {provider.accountInfo.balance} {provider.accountInfo.currency}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>الرسائل المرسلة:</strong> {provider.statistics.totalMessagesSent}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>أسماء المرسلين:</strong> {provider.apiConfig.senderNames.length}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Tooltip title="تعديل">
                      <IconButton size="small" onClick={() => handleOpenDialog(provider)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {!provider.isDefault && (
                      <>
                        <Tooltip title="تعيين كافتراضي">
                          <IconButton size="small" onClick={() => handleSetDefault(provider._id)}>
                            <StarBorderIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton size="small" onClick={() => handleDeleteProvider(provider._id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="تحديث الرصيد">
                      <IconButton size="small" onClick={() => handleCheckBalance(provider._id)}>
                        <BalanceIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* نموذج إضافة/تعديل مزود */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingProvider ? 'تعديل مزود' : 'إضافة مزود جديد'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={dialogTab} onChange={(e, newValue) => setDialogTab(newValue)}>
                <Tab label="المعلومات الأساسية" />
                <Tab label="إعدادات الإرسال" />
                <Tab label="التنبيهات" />
                {editingProvider && <Tab label="الإحصائيات" />}
              </Tabs>
            </Box>

            {/* تبويب المعلومات الأساسية */}
            {dialogTab === 0 && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="اسم المزود"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="المعرف"
                    value={formData.identifier}
                    onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                    required
                    disabled={!!editingProvider}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>نوع المزود</InputLabel>
                    <Select
                      value={formData.providerType}
                      label="نوع المزود"
                      onChange={(e) => setFormData({ ...formData, providerType: e.target.value })}
                      disabled={!!editingProvider}
                    >
                      <MenuItem value="taqnyat">تقنيات (Taqnyat)</MenuItem>
                      <MenuItem value="twilio">Twilio</MenuItem>
                      <MenuItem value="nexmo">Nexmo</MenuItem>
                      <MenuItem value="other">أخرى</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="رابط API"
                    value={formData.apiConfig.baseUrl}
                    onChange={(e) => setFormData({
                      ...formData,
                      apiConfig: { ...formData.apiConfig, baseUrl: e.target.value }
                    })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bearer Token"
                    type="password"
                    value={formData.apiConfig.bearerToken}
                    onChange={(e) => setFormData({
                      ...formData,
                      apiConfig: { ...formData.apiConfig, bearerToken: e.target.value }
                    })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">أسماء المرسلين</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenSenderDialog(true)}
                    >
                      إضافة
                    </Button>
                  </Box>
                  <List dense>
                    {formData.apiConfig.senderNames.map((sender, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={sender.name}
                          secondary={sender.isDefault ? 'افتراضي' : ''}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveSenderName(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="ملاحظات"
                    multiline
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                    }
                    label="نشط"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isDefault}
                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      />
                    }
                    label="افتراضي"
                  />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* تبويب إعدادات الإرسال */}
            {dialogTab === 1 && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      label="الحد الأقصى للمستلمين في الرسالة"
                      value={formData.sendingSettings.maxRecipientsPerMessage}
                      onChange={(e) => setFormData({
                        ...formData,
                        sendingSettings: {
                          ...formData.sendingSettings,
                          maxRecipientsPerMessage: parseInt(e.target.value) || 100
                        }
                      })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.sendingSettings.enableRetry}
                          onChange={(e) => setFormData({
                            ...formData,
                            sendingSettings: {
                              ...formData.sendingSettings,
                              enableRetry: e.target.checked
                            }
                          })}
                        />
                      }
                      label="تفعيل إعادة المحاولة عند الفشل"
                    />
                  </Grid>
                  {formData.sendingSettings.enableRetry && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="عدد محاولات الإعادة"
                          value={formData.sendingSettings.maxRetryAttempts}
                          onChange={(e) => setFormData({
                            ...formData,
                            sendingSettings: {
                              ...formData.sendingSettings,
                              maxRetryAttempts: parseInt(e.target.value) || 3
                            }
                          })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="الفترة بين المحاولات (دقائق)"
                          value={formData.sendingSettings.retryDelayMinutes}
                          onChange={(e) => setFormData({
                            ...formData,
                            sendingSettings: {
                              ...formData.sendingSettings,
                              retryDelayMinutes: parseInt(e.target.value) || 5
                            }
                          })}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            )}

            {/* تبويب التنبيهات */}
            {dialogTab === 2 && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.notifications.notifyOnSuccess}
                          onChange={(e) => setFormData({
                            ...formData,
                            notifications: {
                              ...formData.notifications,
                              notifyOnSuccess: e.target.checked
                            }
                          })}
                        />
                      }
                      label="إرسال تنبيه عند نجاح الإرسال"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.notifications.notifyOnFailure}
                          onChange={(e) => setFormData({
                            ...formData,
                            notifications: {
                              ...formData.notifications,
                              notifyOnFailure: e.target.checked
                            }
                          })}
                        />
                      }
                      label="إرسال تنبيه عند فشل الإرسال"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="email"
                      label="البريد الإلكتروني للتنبيهات"
                      value={formData.notifications.notificationEmail}
                      onChange={(e) => setFormData({
                        ...formData,
                        notifications: {
                          ...formData.notifications,
                          notificationEmail: e.target.value
                        }
                      })}
                      helperText="سيتم إرسال التنبيهات إلى هذا البريد"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* تبويب الإحصائيات (للتعديل فقط) */}
            {dialogTab === 3 && editingProvider && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          إجمالي الرسائل
                        </Typography>
                        <Typography variant="h4">
                          {editingProvider.statistics.totalMessagesSent}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          الرسائل الناجحة
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {editingProvider.statistics.totalSuccessfulMessages}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          الرسائل الفاشلة
                        </Typography>
                        <Typography variant="h4" color="error.main">
                          {editingProvider.statistics.totalFailedMessages}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          التكلفة الإجمالية
                        </Typography>
                        <Typography variant="h4">
                          {editingProvider.statistics.totalCost.toFixed(2)} {editingProvider.accountInfo.currency}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          الرصيد الحالي
                        </Typography>
                        <Typography variant="h5">
                          {editingProvider.accountInfo.balance} {editingProvider.accountInfo.currency}
                        </Typography>
                        {editingProvider.accountInfo.lastBalanceUpdate && (
                          <Typography variant="caption" color="text.secondary">
                            آخر تحديث: {new Date(editingProvider.accountInfo.lastBalanceUpdate).toLocaleString('ar-SA')}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>إلغاء</Button>
            <Button onClick={handleSaveProvider} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'حفظ'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* نموذج إضافة اسم مرسل */}
        <Dialog open={openSenderDialog} onClose={() => setOpenSenderDialog(false)}>
          <DialogTitle>إضافة اسم مرسل</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="اسم المرسل"
                value={newSenderName}
                onChange={(e) => setNewSenderName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={senderIsDefault}
                    onChange={(e) => setSenderIsDefault(e.target.checked)}
                  />
                }
                label="تعيين كافتراضي"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSenderDialog(false)}>إلغاء</Button>
            <Button onClick={handleAddSenderName} variant="contained">
              إضافة
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default SmsProvidersPage;